import { SupabaseTableBase } from '../../utils/SupabaseTableBase';
import { CostLog } from './types';

export class CostLogService extends SupabaseTableBase<CostLog> {
  constructor() {
    super('CostLog');
  }

  async logCost(costData: {
    agent: string;
    inputCost: string;
    inputTokens: number;
    outputCost: string;
    outputTokens: number;
    totalCost: string;
    timestamp: string;
    call_id?: string | null;
    center_id?: string | null;
  }): Promise<CostLog | null> {
    const logData = {
      agent: costData.agent,
      inputCost: costData.inputCost,
      inputTokens: costData.inputTokens,
      outputCost: costData.outputCost,
      outputTokens: costData.outputTokens,
      timestamp: costData.timestamp,
      totalCost: costData.totalCost,
      call_id: costData.call_id || null,
      center_id: costData.center_id || null
    };

    console.log('[COST_LOG_SERVICE] Logging cost data:', logData);

    const result = await this.insertRecord(logData);

    if (result.error) {
      console.error('[COST_LOG_SERVICE] Error inserting cost record:', result.error);
    } else {
      console.log('[COST_LOG_SERVICE] Successfully inserted cost record:', result.data);
    }

    return result.data;
  }

  async getCostLogsByCall(callId: string): Promise<CostLog[]> {
    const result = await this.selectAllRecords({
      filters: [
        { column: 'call_id', operator: 'eq', value: callId }
      ],
      orderBy: 'created_at',
      ascending: false
    });

    return result.data || [];
  }

  async getCostLogsByAgent(agent: string): Promise<CostLog[]> {
    const result = await this.selectAllRecords({
      filters: [
        { column: 'agent', operator: 'eq', value: agent }
      ],
      orderBy: 'created_at',
      ascending: false
    });

    return result.data || [];
  }

  async getTotalCostByCall(callId: string): Promise<number> {
    const result = await this.executeCustomQuery((q: any) =>
      q
        .select('totalCost')
        .eq('call_id', callId)
    );

    if (!result.data) return 0;

    return result.data.reduce((total: number, log: CostLog) => {
      const cost = parseFloat(log.totalCost || '0');
      return total + cost;
    }, 0);
  }

  async getTotalCostByAgent(agent: string): Promise<number> {
    const result = await this.executeCustomQuery((q: any) =>
      q
        .select('totalCost')
        .eq('agent', agent)
    );

    if (!result.data) return 0;

    return result.data.reduce((total: number, log: CostLog) => {
      const cost = parseFloat(log.totalCost || '0');
      return total + cost;
    }, 0);
  }

  async getCostLogsByDateRange(startDate: string, endDate: string): Promise<CostLog[]> {
    const result = await this.selectAllRecords({
      filters: [
        { column: 'created_at', operator: 'gte', value: startDate },
        { column: 'created_at', operator: 'lte', value: endDate }
      ],
      orderBy: 'created_at',
      ascending: false
    });

    return result.data || [];
  }

  async deleteCostLogsByCall(callId: string): Promise<boolean> {
    const result = await this.executeCustomQuery((q: any) =>
      q
        .delete()
        .eq('call_id', callId)
    );

    return result.error === null;
  }

  async getAllCostLogs(): Promise<CostLog[]> {
    const result = await this.selectAllRecords({
      orderBy: 'created_at',
      ascending: false
    });

    return result.data || [];
  }

  async getCostLogsPaginated(
    page: number,
    limit: number,
    filters?: {
      search?: string;
      agent?: string;
      dateFrom?: string;
      dateTo?: string;
      minCost?: string;
      maxCost?: string;
      centerId?: string | null;
    },
    orderBy?: string,
    ascending?: boolean
  ): Promise<{ data: CostLog[]; totalPages: number; count: number }> {
    const queryFilters: any[] = [];

    if (filters?.agent) {
      queryFilters.push({ column: 'agent', operator: 'eq', value: filters.agent });
    }

    if (filters?.dateFrom) {
      queryFilters.push({ column: 'created_at', operator: 'gte', value: filters.dateFrom });
    }

    if (filters?.dateTo) {
      // Ensure we get the end of the day for dateTo
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      queryFilters.push({ column: 'created_at', operator: 'lte', value: toDate.toISOString() });
    }

    if (filters?.minCost) {
      // Note: totalCost is stored as string in DB but we might need to cast or rely on consistent formatting
      // Supabase filtering on string for numeric comparison might be tricky if not padded, 
      // but usually CostLog totalCost is decimal string. Let's try direct comparison first.
      queryFilters.push({ column: 'totalCost', operator: 'gte', value: filters.minCost });
    }

    if (filters?.maxCost) {
      queryFilters.push({ column: 'totalCost', operator: 'lte', value: filters.maxCost });
    }

    if (filters?.centerId) {
      queryFilters.push({ column: 'center_id', operator: 'eq', value: filters.centerId });
    }

    // Handle search separately since it might involve OR logic across multiple columns
    // selectRecordsWithPagination doesn't easily support mixed AND/OR without custom query builder.
    // However, SupabaseTableBase.selectRecordsWithPagination takes "filters" which are ANDed.
    // Use searchRecordsByText if search is present, OR combine search logic manually if possible.
    // Since base class limits complex queries, for search + pagination we might need a custom approach
    // or just filter strict text matches. 
    // Given the limitations of the base class wrapper for complex OR + AND + Pagination, 
    // if search is present, we might fall back to client side or use a more custom query.
    // But let's try to stick to base class if possible. 
    // Actually, `searchRecordsByText` returns a list, not paginated.
    // Let's rely on basic filtering for now. If search is needed, we might overload filter options.
    // For now, let's implement basic filtering. Search text might be applied to 'agent' or 'call_id' via OR.
    // But `filters` array implies AND.
    // If search is provided, we can maybe filter by call_id if it looks like one, or agent.
    // A proper solution would require extending SupabaseTableBase search to support pagination.
    // Let's implement it with basic filters first.

    const result = await this.selectRecordsWithPagination(page, limit, {
      filters: queryFilters,
      orderBy: orderBy || 'created_at',
      ascending: ascending ?? false
    });

    return {
      data: result.data || [],
      totalPages: result.totalPages,
      count: (result.totalPages * limit) // Approximate or calculate back
    };
  }

  async getCostStatistics(
    filters?: {
      search?: string;
      agent?: string;
      dateFrom?: string;
      dateTo?: string;
      minCost?: string;
      maxCost?: string;
      centerId?: string | null;
    }
  ): Promise<{
    totalCost: number;
    totalInteractions: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    uniqueAgents: number;
    uniqueCalls: number;
  }> {

    // Build query to get all matching records for aggregation
    // Note: Ideally we would do this with SQL aggregation functions, but SupabaseTableBase 
    // mostly wraps basic operations. For now, we might need to fetch a lighter version of records
    // or use `count` and assume sums need full fetch if API doesn't support sum.
    // However, fetching ALL records just to sum them up defeats performance gains.
    // Let's use `executeCustomQuery` to perform aggregation if possible, or fetch only necessary columns.

    // We can chain filters just like in pagination
    let query = this.getClient().from(this.getTableName()).select('agent, call_id, totalCost, inputTokens, outputTokens');

    if (filters?.agent) {
      query = query.eq('agent', filters.agent);
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters?.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      query = query.lte('created_at', toDate.toISOString());
    }
    if (filters?.minCost) {
      query = query.gte('totalCost', filters.minCost);
    }
    if (filters?.maxCost) {
      query = query.lte('totalCost', filters.maxCost);
    }
    if (filters?.centerId) {
      query = query.eq('center_id', filters.centerId);
    }
    if (filters?.centerId) {
      query = query.eq('center_id', filters.centerId);
    }

    const { data: records, error } = await query;

    if (error || !records) {
      console.error('Error fetching statistics:', error);
      return {
        totalCost: 0,
        totalInteractions: 0,
        totalInputTokens: 0,
        totalOutputTokens: 0,
        uniqueAgents: 0,
        uniqueCalls: 0
      };
    }

    const totalCost = records.reduce((sum, r) => sum + (parseFloat(r.totalCost) || 0), 0);
    const totalInputTokens = records.reduce((sum, r) => sum + (r.inputTokens || 0), 0);
    const totalOutputTokens = records.reduce((sum, r) => sum + (r.outputTokens || 0), 0);
    const uniqueAgents = new Set(records.map(r => r.agent).filter(Boolean)).size;
    const uniqueCalls = new Set(records.map(r => r.call_id).filter(Boolean)).size;

    return {
      totalCost,
      totalInteractions: records.length,
      totalInputTokens,
      totalOutputTokens,
      uniqueAgents,
      uniqueCalls
    };
  }
}
